import { useState } from 'react'
import { AddressInfo, customerData } from '../utilities/CustomerData'
import { Autocomplete, useLoadScript } from '@react-google-maps/api'
import { apiURL } from '../utilities/URLs'
import { el } from 'date-fns/locale'
import { refreshAccessToken } from '../utilities/extensions'
import { useNavigate } from 'react-router-dom'

export default function MissplacedCheck({ setReload }) {
  const navigate = useNavigate()

  const [street, setStreet] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zip, setZip] = useState('')
  const [autocomplete, setAutocomplete] = useState(null)
  const [errors, setErrors] = useState({})
  const [fetchingLabel, setFetchingLabel] = useState(false)
  const [labelURL, setLabelURL] = useState('')

  const libraries = ['places']

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  })

  const handleLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance)
  }

  const handlePlaceChanged = () => {
    const getAddressComponent = (type) => {
      const component = place.address_components.find(comp => comp.types.includes(type))
      return component ? component.long_name : ''
    }

    const place = autocomplete.getPlace()
    if (place) {
      setStreet(`${getAddressComponent('street_number')} ${getAddressComponent('route')}`.trim())
      setCity(getAddressComponent('locality'))
      setState(getAddressComponent('administrative_area_level_1'))
      setZip(getAddressComponent('postal_code'))
    }
  }

  const handleCheckAddress = async () => {
    const newErrors = {}

    if (!street) newErrors.street = 'Street is required'
    if (!city) newErrors.city = 'City is required'
    if (!state) newErrors.state = 'State is required'
    if (!zip) newErrors.zip = 'Zip is required'

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    await handleGetLabel()
  }

  const handleGetLabel = async () => {
    setFetchingLabel(true)

    const form = new FormData
    form.append('missplacedCheckInfo', JSON.stringify({
      leadId: customerData.customerLeadData.leadId,
      name: customerData.customerLeadData.leadContact.contactName,
      street,
      city,
      state,
      zip,
      phone: customerData.customerLeadData.leadContact.contactPhone,
      email: customerData.customerLeadData.leadContact.contactEmail
    }))

    const labelResponse = await fetch(`${apiURL}/api/missplaced-check-label/`, {
      method: 'POST',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const labelData = await labelResponse.json()
    if(labelData.response.error) {
      if(labelData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleGetLabel() : navigate('/', { state: { expired: true } })
      } else
        console.error(labelData.response.error)
    } else
      setLabelURL(labelData.response.labelURL)

    setFetchingLabel(false)
  }

  return (
    <div className='w-screen flex flex-1 flex-col items-center gap-4'>
      <p className='font-bold text-2xl mt-4'>Address</p>
      <div>
        <p className='text-center mb-6'>Please enter your address for your shipping label.</p>
        <div className='relative mb-4'>
          <p className='absolute text-sm bg-white left-3 top-[-5px]'>Street</p>
          {isLoaded && (
            <Autocomplete onLoad={handleLoad} onPlaceChanged={handlePlaceChanged}>
              <input
                id='autocomplete'
                type='text'
                name='street'
                placeholder='Street'
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                className={`mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 
              ${errors.street ? 'border-red-500' : 'focus:ring-indigo-300'}`}
              />
            </Autocomplete>
        )}
          {errors.street && <p className='text-red-500 text-xs mt-1 w-full text-left'>{errors.street}</p>}
        </div>
        <div className='relative mb-4'>
          <p className='absolute text-sm bg-white left-3 top-[-5px]'>City</p>
          <input
            type='text'
            name='city'
            placeholder='City'
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className={`mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 
          ${errors.city ? 'border-red-500' : 'focus:ring-indigo-300'}`}
          />
          {errors.city && <p className='text-red-500 text-xs mt-1 w-full text-left'>{errors.city}</p>}
        </div>
        <div className='relative mb-4'>
          <p className='absolute text-sm bg-white left-3 top-[-5px]'>State</p>
          <input
            type='text'
            name='state'
            placeholder='State'
            value={state}
            onChange={(e) => setState(e.target.value)}
            className={`mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 
          ${errors.state ? 'border-red-500' : 'focus:ring-indigo-300'}`}
          />
          {errors.state && <p className='text-red-500 text-xs mt-1 w-full text-left'>{errors.state}</p>}
        </div>
        <div className='relative mb-4'>
          <p className='absolute text-sm bg-white left-3 top-[-5px]'>Zip</p>
          <input
            type='text'
            name='zip'
            placeholder='Zip'
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            className={`mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 
          ${errors.zip ? 'border-red-500' : 'focus:ring-indigo-300'}`}
          />
          {errors.zip && <p className='text-red-500 text-xs mt-1 w-full text-left'>{errors.zip}</p>}
        </div>
        <div className='flex w-full justify-center'>
          <button
            onClick={handleCheckAddress}
            disabled={fetchingLabel}
            className='px-8 py-2 mt-4 bg-primaryColor hover:bg-primaryDarkColor transition duration-200 text-white rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 max-sm:mb-4 disabled:bg-gray-400 disabled:cursor-not-allowed'>
            Get Label
          </button>
        </div>
        {fetchingLabel && (
          <div className='flex flex-row items-center justify-center gap-2 -mb-5'>
            <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
            <p>Generating Label, Please Wait...</p>
          </div>
        )}
      </div>
      {labelURL && (
        <div className='fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white shadow-md rounded-lg p-6 lg:w-full w-11/12 max-w-lg text-center relative'>
            <h2 className='text-2xl font-semibold mb-2'>Label Created!</h2>
            <p className='text-gray-600 mb-6'>Your label has been created. Please click the button below to download your label. Note that you can always view this label on your dashboard.</p>
            <p className='text-gray-600 mb-6'>Please make sure to use this label when mailing your check so it can get to us in a timely manner.</p>
            <div className='flex justify-between'>
              <button onClick={() => window.open(labelURL)} className='bg-primaryColor hover:bg-primaryDarkColor transition duration-200 text-white px-4 py-2 rounded'>Download Label</button>
              <button onClick={() => { setReload(true); navigate('/dashboard') }} className='bg-secondaryColor hover:bg-secondaryDarkColor transition duration-200 text-white px-4 py-2 rounded'>Return To Dashboard</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}